import { vacancyApiActions } from '@mkp/vacancy/state/actions';
import { legalEntityGuardActions } from '@mkp/legal-entity/actions';
import { Account } from '@mkp/account/data-access';
import { accountApiActions } from '@mkp/account/data-access/actions';
import { authActions, boAuthActions } from '@mkp/auth/actions';
import { claimManagementActions, legalEntityDetailsActions } from '@mkp/legal-entity/actions';
import { FetchState, LoadingState } from '@mkp/shared/util-state';
import { accountAggregateApiActions } from '@mkp/user-account-membership/actions';
import { BoDashboardActions } from '@mkp/user/feature-bo-dashboard/actions';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';

interface State extends EntityState<Account> {
  fetchState: FetchState<{ message: string }>;
  selectedAccountId: Account['id'] | undefined;
}

const adapter: EntityAdapter<Account> = createEntityAdapter<Account>();

const initialState: State = adapter.getInitialState({
  fetchState: LoadingState.INIT,
  selectedAccountId: undefined,
});

const reducer = createReducer(
  initialState,
  on(accountApiActions.addAccountSuccess, (state) => ({
    ...state,
    fetchState: LoadingState.LOADED,
  })),
  on(accountApiActions.addAccountFailure, (state, { error }) => ({
    ...state,
    fetchState: { error },
  })),
  // these actions set the state to LOADING because they trigger a call in AccountResource
  on(
    authActions.verifiedUserAuthenticated,
    legalEntityGuardActions.canActivate,
    vacancyApiActions.listVacanciesSuccess,
    vacancyApiActions.loadMoreVacanciesSuccess,
    claimManagementActions.loadAccountFromSelectedLegalEntity,
    legalEntityDetailsActions.pageEntered,
    BoDashboardActions.enter,
    BoDashboardActions.initDropdown,
    vacancyApiActions.loadVacanciesByIdsSuccess,
    boAuthActions.loadWorkspace,
    accountApiActions.loadUserAccounts,
    accountApiActions.addAccount,
    accountApiActions.archiveAccount,
    (state) => ({
      ...state,
      fetchState: LoadingState.LOADING,
    })
  ),
  on(authActions.noAccountsFoundAfterUserLogin, (state) => ({
    ...state,
    fetchState: LoadingState.LOADED,
  })),
  on(authActions.workspaceSelected, (state, { accountId }) => ({
    ...state,
    selectedAccountId: accountId,
  })),
  on(
    authActions.loadAccountsAfterLoginSuccess,
    authActions.loadWorkspaceAccountSuccess,
    authActions.workspaceFoundInStore,
    (state, { accounts }) =>
      adapter.upsertMany(accounts, {
        ...state,
        selectedAccountId: accounts.length === 1 ? accounts[0].id : undefined,
        fetchState: LoadingState.LOADED,
      })
  ),
  // these actions save an account in store and select an account at the same time
  on(
    accountApiActions.loadAccountByLegalEntityExternalIdSuccess,
    (state, { account, selectedAccountId }) =>
      adapter.upsertOne(account, { ...state, selectedAccountId, fetchState: LoadingState.LOADED })
  ),
  // these actions save an account in store
  on(
    accountApiActions.loadAccountSuccess,
    accountApiActions.archiveAccountSuccess,
    accountApiActions.refreshSelectedAccountSuccess,
    (state, { account }) =>
      adapter.upsertOne(account, { ...state, fetchState: LoadingState.LOADED })
  ),
  // refresh accounts after a user self-deletes their own account-membership
  on(
    accountApiActions.refreshAllAccountsSuccess,
    accountAggregateApiActions.refreshOnPermissionCheckSuccess,
    accountAggregateApiActions.refreshOnRedeemInviteSuccess,
    (state, { accounts }) =>
      adapter.setAll(accounts, {
        ...state,
        fetchState: LoadingState.LOADED,
        selectedAccountId: accounts.length === 1 ? accounts[0].id : undefined,
      })
  ),
  on(
    accountAggregateApiActions.refreshOnPermissionCheckFailure,
    accountAggregateApiActions.refreshOnRedeemInviteFailure,
    (state) =>
      adapter.setAll([], {
        ...state,
        fetchState: LoadingState.LOADED,
        selectedAccountId: undefined,
      })
  ),
  // these action save multiple accounts in store
  on(
    accountApiActions.loadAccountsSuccess,
    accountApiActions.loadUserAccountsSuccess,
    authActions.loadAccountsAfterBOLoginSuccess,
    (state, { accounts }) =>
      adapter.upsertMany(accounts, { ...state, fetchState: LoadingState.LOADED })
  ),
  // reset accounts on legal entity selection
  on(legalEntityDetailsActions.accountsLoadSuccess, (state, { accounts }) =>
    adapter.setAll(accounts, { ...state, fetchState: LoadingState.LOADED })
  ),
  // these actions unselect an account
  on(boAuthActions.workspaceUnselected, authActions.workspaceSelectPageEntered, (state) => ({
    ...state,
    selectedAccountId: undefined,
  })),
  // these actions set the state to ERROR
  on(
    authActions.loadAccountsAfterLoginFailed,
    authActions.loadWorkspaceAccountError,
    accountApiActions.loadAccountFailure,
    accountApiActions.loadAccountsFailure,
    accountApiActions.loadAccountByLegalEntityExternalIdFailure,
    accountApiActions.archiveAccountFailure,
    accountApiActions.addAccountFailure,
    accountApiActions.refreshSelectedAccountFailure,
    accountApiActions.refreshAllAccountsFailure,
    (state, { error }) => ({
      ...state,
      fetchState: { error },
    })
  )
);

const feature = createFeature({
  name: 'account',
  reducer,
});

const { selectAll } = adapter.getSelectors(feature.selectAccountState);

export const accountFeature = { ...feature, selectAll };
export { State as AccountState };
